import { sendMessage } from "actions";

import { createXrs2iDevice } from "../device/createProtocol";
import type { IRxTransport, ITxTransport } from "../device/types";
import {
  DriverEventType,
  flushDriverEffects,
  mapDeviceEventToDriverEffects,
  type DriverActionResult,
  type DriverEffect,
  type DriverNotifier,
} from "../adapters/driverEffects";
import { toastNotifier } from "../adapters/toastNotifier";

/**
 * Owns one live XRS2i protocol device for a single scanner connection.
 * It collects protocol events, converts them into Redux actions, and flushes delayed work on demand.
 */
export class Xrs2iDriverSession {
  private readonly effects: DriverEffect[] = [];

  private receiveHandler: (payload: string) => void = () => {};

  private readonly tx: ITxTransport;

  private readonly rx: IRxTransport;

  private readonly device: ReturnType<typeof createXrs2iDevice>;

  constructor(
    private readonly deviceId: string,
    private readonly notifier: DriverNotifier = toastNotifier,
  ) {
    const sessionDeviceId = this.deviceId;

    // Protocol write adapter: the device factory expects a Promise-returning send(payload)
    // transport, so we expose that exact shape and convert each write into a queued Redux action.
    this.tx = {
      send: payload => {
        this.effects.push({
          type: DriverEventType.ACTION,
          action: sendMessage(sessionDeviceId, payload),
        });
        return Promise.resolve();
      },
    };

    // Protocol read adapter: the device factory subscribes via onReceive(handler) and requires
    // an unsubscribe callback, so this bridge stores one active handler and resets it on teardown.
    this.rx = {
      onReceive: handler => {
        this.receiveHandler = handler;
        return () => {
          if (this.receiveHandler === handler) {
            this.receiveHandler = () => {};
          }
        };
      },
    };

    this.device = createXrs2iDevice(this.tx, this.rx, event => {
      this.effects.push(
        ...mapDeviceEventToDriverEffects(sessionDeviceId, event),
      );
    });
  }

  /**
   * Starts the saved-scan import sequence for this device.
   * The return value contains any Redux action that should be dispatched right away.
   */
  readSavedScans(): DriverActionResult {
    void this.device.readSavedScans();
    return this.drainEffects();
  }

  /**
   * Rebuilds the device's hardware configuration before normal scan handling continues.
   * The driver uses this when it needs the scanner to confirm its EID output mode first.
   */
  syncState(): DriverActionResult {
    void this.device.resetHardwareConfig();
    return this.drainEffects();
  }

  /**
   * Cancels the active saved-scan import and clears the current command pipeline.
   * This allows the device to ignore stale late frames without tearing the whole session down.
   */
  cancelReadSavedScans(): DriverActionResult {
    this.device.cancelReadSavedScans();
    return this.drainEffects();
  }

  /**
   * Delivers a raw payload to the live protocol device and drains any resulting Redux actions.
   * This keeps frame parsing and action creation in one place so callers only forward payloads.
   */
  receive(payload: string): DriverActionResult {
    this.receiveHandler(payload);
    return this.drainEffects();
  }

  /**
   * Flushes timer-driven work such as retry sends or deferred completion actions into Redux.
   * The saga layer dispatches the returned actions so delayed protocol work stays observable.
   */
  tick(): DriverActionResult {
    return this.drainEffects();
  }

  /**
   * Tears down the live protocol device and clears any queued work for this scanner session.
   * This prevents retries and callbacks from surviving after the caller has finished with the device.
   */
  destroy(): void {
    this.device.destroy();
    this.effects.length = 0;
  }

  private drainEffects(): DriverActionResult {
    return flushDriverEffects(this.effects, this.notifier);
  }
}
